import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from '../locales/en.json';
import de from '../locales/de.json';
import fr from '../locales/fr.json';
import ja from '../locales/ja.json';
import es from '../locales/es.json';
import it from '../locales/it.json';
import nl from '../locales/nl.json';

const SUPPORTED_LANGUAGES = ['en', 'de', 'fr', 'ja', 'es', 'it', 'nl'] as const;
type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

function isSupportedLanguage(lang: string): lang is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(lang);
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      de: { translation: de },
      fr: { translation: fr },
      ja: { translation: ja },
      es: { translation: es },
      it: { translation: it },
      nl: { translation: nl },
    },
    // Do NOT set a static `lng` property — it bypasses the LanguageDetector entirely.
    // Let the detector cascade: localStorage → navigator → fallbackLng.
    fallbackLng: 'en',
    supportedLngs: ['en', 'de', 'fr', 'ja', 'es', 'it', 'nl'],
    // When the detected language is not in supportedLngs, fall back to a supported one
    // rather than using the raw detected value (e.g. 'en-US' → 'en').
    nonExplicitSupportedLngs: true,
    detection: {
      // Detection order: first check localStorage, then browser navigator language
      order: ['localStorage', 'navigator'],
      // The localStorage key where the user's language preference is persisted
      lookupLocalStorage: 'reefguide.language',
      // Automatically persist the language to localStorage on init and on changeLanguage()
      caches: ['localStorage'],
      // Validate that the cached localStorage value is in our supported languages before
      // using it — prevents poisoned or stale values from breaking the app.
      convertDetectedLanguage: (lng: string): string => {
        // Strip region suffix (e.g. 'en-US' → 'en') before validation
        const base = lng.split('-')[0] ?? lng;
        return isSupportedLanguage(base) ? base : 'en';
      },
    },
    interpolation: {
      // React already escapes output — no need for i18next to escape again
      escapeValue: false,
    },
    returnNull: false,
    returnEmptyString: false,
    react: {
      useSuspense: false,
    },
  })
  .catch((err: unknown) => {
    process.stderr?.write?.(
      `i18n initialisation error: ${err instanceof Error ? err.message : String(err)}\n`,
    );
  });

// Update <html lang> on every language change for WCAG SC 3.1.1 compliance.
// This listener fires on init (via the 'initialized' event path inside i18next) and
// on every subsequent changeLanguage() call.
i18n.on('languageChanged', (lng: string) => {
  const base = lng.split('-')[0] ?? lng;
  const resolved = isSupportedLanguage(base) ? base : 'en';
  if (typeof document !== 'undefined') {
    document.documentElement.lang = resolved;
  }
});

// Set the initial <html lang> attribute once i18next has resolved the language.
// The 'initialized' event fires after init() resolves and the resolved language is known.
i18n.on('initialized', () => {
  const resolved = i18n.resolvedLanguage ?? 'en';
  const base = resolved.split('-')[0] ?? resolved;
  const lang = isSupportedLanguage(base) ? base : 'en';
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lang;
  }
});

export default i18n;
export { i18n, isSupportedLanguage, SUPPORTED_LANGUAGES };
export type { SupportedLanguage };
